import { TransactionContext } from "@/app/contexts/TransactionContext";
import { Transaction } from "@/app/entities/Transaction";
import { useAuth } from "@/app/hooks/useAuth";
import { handleTransactionDocuments } from "@/app/services/transactionService/handleDocuments";
import { TransactionByIdResponse } from "@/app/services/transactionService/transactionById";
import { selectedIdfromTableRowTransaction } from "@/app/store";
import { handleDownload } from "@/app/utils/download";
import { Dropzone } from "@/view/components/Dropzone";
import { Button } from "@/view/components/ui/button";
import { Input } from "@/view/components/ui/input";
import { toast } from "@/view/components/ui/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { Download, FileText, ImageIcon, Trash2 } from "lucide-react";
import { useContext, useEffect, useState } from "react";

type DocItem =
  | {
      id: string;
      file: File;
      preview?: string;
      customName?: string;
      url?: never;
    }
  | { id: string; url: string; name?: string; file?: never };

const nameFromUrl = (url: string) => {
  try {
    const segment = url.split("/").pop() ?? url;
    return decodeURIComponent(segment);
  } catch {
    return url;
  }
};

const normalizeDocuments = (documents: string[]) => {
  return documents.map((url) => ({
    id: url,
    url,
    name: nameFromUrl(url),
  }));
};
const sanitizeFileName = (name: string) => name.replace(/\s+/g, "_");

export const UploadFiles = ({
  data,
  onBack,
}: {
  data?: Transaction | null;
  onBack: () => void;
}) => {
  const { isSeller } = useAuth();
  const [documents, setDocuments] = useState<DocItem[]>([]);
  const [removeFiles, setRemoveFiles] = useState<string[]>([]);
  const [nextFileName, setNextFileName] = useState("");

  const queryClient = useQueryClient();
  const [selectedId] = useAtom(selectedIdfromTableRowTransaction);

  const { refetch } = useContext(TransactionContext);

  const transactionId = data?._id ?? selectedId;
  const transaction = queryClient.getQueryData<TransactionByIdResponse>([
    "transactions",
    "transactionById",
    transactionId,
  ]);

  const canUpload = transaction?.config?.canUploadDocs && isSeller;

  const addFile = (file: File) => {
    const name = sanitizeFileName(file.name);
    const fileWithoutSpaces =
      name !== file.name ? new File([file], name, { type: file.type }) : file;
    const customName = nextFileName.trim() || fileWithoutSpaces.name;
    const newFile: DocItem = {
      id: crypto.randomUUID(),
      file: fileWithoutSpaces,
      customName,
      preview: file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : undefined,
    };
    setDocuments((prev) => [...prev, newFile]);
    setNextFileName("");
  };

  const getDisplayName = (doc: DocItem): string => {
    if ("file" in doc && doc.file) return doc.customName ?? doc.file.name;
    if ("url" in doc) return doc.name ?? "";
    return "";
  };

  const uploadBox = () => (
    <Dropzone
      className="border-gray-300"
      value={null}
      onDrop={(acceptedFiles) => {
        const file = acceptedFiles?.[0];
        if (file) addFile(file);
      }}
      dropzoneOptions={{
        multiple: false,
        accept: {
          "image/*": [],
          "application/pdf": [],
        },
      }}
      render={() => (
        <div className="relative flex h-10 w-full flex-col items-center justify-center rounded-lg bg-transparent">
          <div className="flex flex-col items-center text-gray-400">
            <ImageIcon className="mb-2 h-8 w-8 opacity-50" />
            <span className="text-sm font-semibold text-black01">
              Adicionar documento
            </span>
          </div>
        </div>
      )}
    />
  );

  const removeFile = (id: string) => {
    setDocuments((prev) => {
      const doc = prev.find((d) => d.id === id);
      if (!doc) return prev;
      if ("url" in doc && doc.url) {
        const urlToRemove: string = doc.url;
        setRemoveFiles((urls) =>
          urls.includes(urlToRemove) ? urls : [...urls, urlToRemove],
        );
      } else {
        const withPreview = doc as { preview?: string };
        if (withPreview.preview) URL.revokeObjectURL(withPreview.preview);
      }
      return prev.filter((d) => d.id !== id);
    });
  };

  const newFiles = documents.filter(
    (d): d is DocItem & { file: File } => "file" in d && d.file != null,
  );

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async () => {
      if (!transaction?._id) return;
      const filesToSend = newFiles.map((d) => {
        const name = sanitizeFileName(d.customName?.trim() || d.file.name);
        return name !== d.file.name
          ? new File([d.file], name, { type: d.file.type })
          : d.file;
      });
      return handleTransactionDocuments({
        id: transaction._id,
        files: filesToSend,
        removeFiles,
      });
    },
  });

  const onSubmit = async () => {
    try {
      const result = await mutateAsync();
      const newDocuments = normalizeDocuments(
        result?.transaction?.support?.documents ?? [],
      );
      setRemoveFiles([]);
      refetch();
      setDocuments(newDocuments);
      toast({
        title: "Documentos salvos com sucesso",
      });
      onBack();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao salvar documentos",
      });
    }
  };

  const downloadFile = (doc: DocItem) => {
    if ("url" in doc && doc.url) {
      handleDownload({
        url: `${import.meta.env.VITE_CDN_URL}${doc.url}`,
        name: doc.name,
      });
    } else if ("file" in doc && doc.file) {
      handleDownload({ file: doc.file });
    }
  };

  useEffect(() => {
    const existingUrls = transaction?.support?.documents ?? [];
    const existing: DocItem[] = normalizeDocuments(existingUrls);
    setDocuments(existing);
    setRemoveFiles([]);
  }, [transaction?._id]);

  return (
    <div className="flex flex-1 flex-col gap-3">
      {canUpload && (
        <>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700">
              Título (opcional)
            </label>
            <Input
              value={nextFileName}
              onChange={(e) => setNextFileName(e.target.value)}
              placeholder="Insira o título do documento"
              className="w-full bg-transparent text-sm"
            />
          </div>
          {uploadBox()}
        </>
      )}

      <div className="mt-2 flex flex-1 flex-col gap-2 overflow-y-auto pr-1">
        {documents.map((doc) => (
          <div
            // TODO: download?
            key={doc.id}
            className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-300 bg-transparent p-3"
          >
            <FileText className="h-5 w-5 shrink-0" />
            <span className="flex-1 truncate text-sm text-gray-700">
              {getDisplayName(doc)}
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                downloadFile(doc);
              }}
              className="shrink-0 rounded  text-gray-400 transition-colors hover:text-primary"
            >
              <Download className="h-4 w-4" />
            </button>
            {canUpload && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  removeFile(doc.id);
                }}
                className="shrink-0 rounded p-1 text-gray-400 transition-colors hover:text-red-500"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {canUpload && (
        <div className="mt-2">
          <Button
            className="w-full text-white"
            onClick={onSubmit}
            disabled={isPending}
          >
            Salvar
          </Button>
        </div>
      )}
    </div>
  );
};
